/**
 * Routeur minimal sur l'History API. Deux routes de jeu suffisent, ajouter une
 * dependance de routage pour ca ne se justifierait pas (cf. CLAUDE.md).
 */
import { useEffect, useState } from 'react';
import { isValidRoomCode, normalizeRoomCode } from '@retusch/shared';
import { PAYMENTS_VISIBLE } from './payments.js';
import { isLanguage, type Language } from './locales/index.js';

export type Route =
  | { readonly name: 'home' }
  | { readonly name: 'room'; readonly code: string }
  | { readonly name: 'browse' }
  | { readonly name: 'account' }
  | { readonly name: 'purchase' }
  | { readonly name: 'legal' }
  | { readonly name: 'privacy' }
  | { readonly name: 'terms' }
  | { readonly name: 'howToPlay' }
  | { readonly name: 'modes' }
  | { readonly name: 'blog' }
  | { readonly name: 'article'; readonly slug: string }
  | { readonly name: 'sandbox' }
  | { readonly name: 'zones' }
  | { readonly name: 'bot' }
  | { readonly name: 'rush' }
  | { readonly name: 'stats' };

const ROOM_PATTERN = /^\/room\/([^/]+)$/;
/** Un article de blog par URL : c'est la seule facon d'etre indexe a part. */
const ARTICLE_PATTERN = /^\/blog\/([a-z0-9-]+)$/;

/**
 * Pages de contenu, une fois le prefixe de langue retire. Elles existent pour
 * les joueurs ET pour les moteurs : sans elles, le site n'offrait a indexer
 * qu'un ecran de quatre boutons.
 */
function parseContent(path: string): Route | null {
  if (path === '/how-to-play') return { name: 'howToPlay' };
  if (path === '/modes') return { name: 'modes' };
  if (path === '/blog') return { name: 'blog' };
  const article = ARTICLE_PATTERN.exec(path);
  if (article !== null) return { name: 'article', slug: article[1] as string };
  return null;
}

/**
 * Prefixe de langue de l'accueil, ou null.
 *
 * Ces adresses (`/en`, `/de`…) existent pour les MOTEURS : elles portent chacune
 * un titre, une description et un `hreflang` dans leur HTML prerendu (cf.
 * prerender-locales.mjs). Une application d'une seule page n'offre sinon qu'une
 * seule URL a indexer, donc une seule langue.
 *
 * SEUL L'ACCUEIL est prefixe, deliberement. `/room/CODE`, `/browse` et
 * `/account` ne bougent pas : aucun lien d'invitation deja partage ne casse, et
 * le routeur n'a qu'un cas de plus a connaitre au lieu d'un prefixe optionnel
 * devant chacune de ses routes.
 */
export function localeHome(pathname: string): Language | null {
  const { language, rest } = splitLocale(pathname);
  return rest === '/' ? language : null;
}

/**
 * Detache le prefixe de langue d'un chemin, s'il y en a un.
 *
 * LE PREFIXE N'EST RETIRE QUE POUR LES PAGES DE CONTENU. `/room/CODE`,
 * `/browse` et `/account` ne sont jamais prefixes : on ne veut ni casser les
 * liens d'invitation deja partages, ni multiplier par dix-sept les adresses du
 * jeu lui-meme. Un chemin comme `/en/room/ABC` n'existe donc pas, et retombe
 * sur l'accueil comme n'importe quelle adresse inconnue.
 */
export function splitLocale(pathname: string): { language: Language | null; rest: string } {
  const path = pathname.replace(/\/+$/, '') || '/';
  const match = /^\/([a-z]{2})(\/.*)?$/.exec(path);
  const candidate = match?.[1];

  // Le francais n'a pas de prefixe : il vit a la racine, qui a l'anciennete et
  // les liens entrants.
  if (candidate === undefined || candidate === 'fr' || !isLanguage(candidate)) {
    return { language: null, rest: path };
  }
  return { language: candidate, rest: match?.[2] ?? '/' };
}

export function parseRoute(pathname: string): Route {
  const path = pathname.replace(/\/+$/, '') || '/';

  // `/en`, `/de`… : l'accueil ou une page de contenu, dans cette langue. La
  // langue elle-meme est posee par App au chargement, une seule fois.
  const { language, rest } = splitLocale(path);
  if (language !== null) {
    if (rest === '/') return { name: 'home' };
    return parseContent(rest) ?? { name: 'home' };
  }

  if (import.meta.env.DEV) {
    if (path === '/sandbox') return { name: 'sandbox' };
    if (path === '/zones') return { name: 'zones' };
    if (path === '/bot') return { name: 'bot' };
    if (path === '/rush') return { name: 'rush' };
    if (path === '/stats') return { name: 'stats' };
  }

  if (path === '/browse') return { name: 'browse' };
  if (path === '/account') return { name: 'account' };
  // Masquee : la route existe toujours, elle ne mene simplement plus nulle
  // part. Un lien colle ou garde en favori arrive sur l'accueil plutot que sur
  // une page morte (cf. payments.ts, et « aucun cul-de-sac » dans CLAUDE.md).
  if (path === '/purchase') return PAYMENTS_VISIBLE ? { name: 'purchase' } : { name: 'home' };
  const content = parseContent(path);
  if (content !== null) return content;

  if (path === '/legal') return { name: 'legal' };
  if (path === '/privacy') return { name: 'privacy' };
  if (path === '/terms') return { name: 'terms' };

  const match = ROOM_PATTERN.exec(path);
  if (match !== null) {
    const code = normalizeRoomCode(decodeURIComponent(match[1] as string));
    if (isValidRoomCode(code)) return { name: 'room', code };
  }

  return { name: 'home' };
}

const listeners = new Set<() => void>();

export function navigate(path: string, options: { replace?: boolean } = {}): void {
  if (options.replace === true) window.history.replaceState(null, '', path);
  else window.history.pushState(null, '', path);
  for (const listener of listeners) listener();
}

export function roomPath(code: string): string {
  return `/room/${code}`;
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));

  useEffect(() => {
    const update = (): void => setRoute(parseRoute(window.location.pathname));
    listeners.add(update);
    window.addEventListener('popstate', update);
    return () => {
      listeners.delete(update);
      window.removeEventListener('popstate', update);
    };
  }, []);

  return route;
}
